import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { CONFIG, INSTRUCTION_PRESETS } from './config'
import {
  escapeRegex,
  resetFileInput,
  getWordAtPosition,
  isToken,
  getMaxTokenNumber,
} from './utils'
import { validatePdf, extractTextFromPdf } from './pdf'
import {
  Header,
  TabNav,
  ErrorAlert,
  TextareaToolbar,
  PdfBadge,
  HighlightedTextarea,
  WordSpanRenderer,
  FeedbackModal,
} from './components'

export function App() {
  // ----- State -----
  const [originalText, setOriginalText] = useState('')
  const [maskedText, setMaskedText] = useState('')
  const [redactionMap, setRedactionMap] = useState({})
  const [textToUnmask, setTextToUnmask] = useState('')
  const [unmaskedText, setUnmaskedText] = useState('')
  const [activeTab, setActiveTab] = useState('redact')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [pdfInfo, setPdfInfo] = useState(null)
  const [copiedField, setCopiedField] = useState(null)
  const [showInfo, setShowInfo] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  const [isEditing, setIsEditing] = useState(true)
  const [instructions, setInstructions] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('instructions')
      if (stored !== null) return stored
    }
    return INSTRUCTION_PRESETS[0].text // Default to 'General'
  })
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme')
      if (stored === 'dark' || stored === 'light') return stored
      // Respect system preference
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
    }
    return 'light'
  })

  // ----- Refs -----
  const idCounterRef = useRef(0)
  const copyTimeoutRef = useRef(null)

  // ----- Derived State -----
  const mapEntries = useMemo(() => Object.entries(redactionMap), [redactionMap])

  // ----- Effects -----
  useEffect(() => {
    return () => copyTimeoutRef.current && clearTimeout(copyTimeoutRef.current)
  }, [])

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
    localStorage.setItem('theme', theme)
  }, [theme])

  useEffect(() => {
    localStorage.setItem('instructions', instructions)
  }, [instructions])

  // ----- Callbacks -----
  const generateId = useCallback(() => {
    idCounterRef.current += 1
    return 'xxxxx' + String(idCounterRef.current).padStart(4, '0')
  }, [])

  const toggleMaskWord = useCallback(
    (word) => {
      if (!word || word.includes(' ')) return

      if (isToken(word)) {
        // Unmask: replace token with original word
        setRedactionMap((prev) => {
          const originalWord = prev[word]
          if (originalWord) {
            const tokenRegex = new RegExp(escapeRegex(word), 'g')
            setMaskedText((text) => text.replace(tokenRegex, originalWord))
            const next = { ...prev }
            delete next[word]
            return next
          }
          return prev
        })
      } else {
        // Mask: replace word with token
        setRedactionMap((prev) => {
          if (Object.values(prev).includes(word)) return prev
          const id = generateId()
          const regex = new RegExp(`\\b${escapeRegex(word)}\\b`, 'g')
          setMaskedText((text) => text.replace(regex, id))
          return { ...prev, [id]: word }
        })
      }
    },
    [generateId]
  )

  const handlePdfUpload = useCallback(
    async (e) => {
      const file = e.target.files?.[0]
      if (!file) return

      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setError('Please select a PDF file')
        return
      }

      if (file.size > CONFIG.pdf.maxSize) {
        setError('File too large. Maximum size is 50MB')
        return
      }

      if (Object.keys(redactionMap).length > 0) {
        const confirmed = window.confirm(
          `You have ${Object.keys(redactionMap).length} protected word(s). ` +
            'Uploading a new PDF will clear all current work. Continue?'
        )
        if (!confirmed) {
          resetFileInput(e)
          return
        }
      }

      setIsProcessing(true)
      setError(null)
      setPdfInfo(null)

      try {
        const arrayBuffer = await file.arrayBuffer()

        if (!validatePdf(arrayBuffer)) {
          throw new Error('Invalid PDF file. File does not appear to be a valid PDF.')
        }

        const { text, pages } = await extractTextFromPdf(arrayBuffer)

        setOriginalText(text)
        setMaskedText(text)
        setRedactionMap({})
        idCounterRef.current = 0
        setPdfInfo({
          name: file.name,
          pages,
          size: (file.size / 1024).toFixed(1) + ' KB',
        })
      } catch (err) {
        console.error('PDF processing error:', err)
        setError(err.message || 'Failed to process PDF')
      } finally {
        setIsProcessing(false)
        resetFileInput(e)
      }
    },
    [redactionMap]
  )

  const handleTextChange = useCallback((e) => {
    const text = e.target.value
    setOriginalText(text)
    setMaskedText(text)
    setPdfInfo(null)
    idCounterRef.current = getMaxTokenNumber(text)

    // Preserve mappings for tokens that still exist in the text
    setRedactionMap((prev) => {
      const kept = {}
      for (const [token, original] of Object.entries(prev)) {
        if (text.includes(token)) {
          kept[token] = original
        }
      }
      return kept
    })
  }, [])

  const handleDoubleClick = useCallback(
    (e) => {
      const { selectionStart, selectionEnd, value } = e.target
      if (!value || selectionStart === selectionEnd) return
      toggleMaskWord(value.slice(selectionStart, selectionEnd).trim())
    },
    [toggleMaskWord]
  )

  const handleContextMenu = useCallback(
    (e) => {
      e.preventDefault()
      const { selectionStart, selectionEnd, value } = e.target

      const word =
        selectionStart !== selectionEnd
          ? value.slice(selectionStart, selectionEnd).trim()
          : getWordAtPosition(value, selectionStart)?.word

      if (word) toggleMaskWord(word)
    },
    [toggleMaskWord]
  )

  const handleUnmask = useCallback(() => {
    if (Object.keys(redactionMap).length === 0) {
      setUnmaskedText(textToUnmask)
      return
    }

    const tokens = Object.keys(redactionMap).map(escapeRegex)
    const combinedRegex = new RegExp(tokens.join('|'), 'g')
    setUnmaskedText(textToUnmask.replace(combinedRegex, (match) => redactionMap[match] || match))
  }, [textToUnmask, redactionMap])

  const copyToClipboard = useCallback(async (text, field) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
      copyTimeoutRef.current = setTimeout(() => setCopiedField(null), CONFIG.ui.copyTimeout)
    } catch (err) {
      console.error('Failed to copy:', err)
      setError('Failed to copy to clipboard')
    }
  }, [])

  const clearRedactTab = useCallback(() => {
    setOriginalText('')
    setMaskedText('')
    setRedactionMap({})
    idCounterRef.current = 0
    setPdfInfo(null)
    setError(null)
    setIsEditing(true)
  }, [])

  const clearUnmaskTab = useCallback(() => {
    setTextToUnmask('')
    setUnmaskedText('')
  }, [])

  // ----- Render -----
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900 text-gray-800 dark:text-neutral-200 p-6 transition-colors">
      <div className="max-w-5xl mx-auto">
        <Header
          showInfo={showInfo}
          onToggleInfo={() => setShowInfo(!showInfo)}
          onOpenFeedback={() => setShowFeedback(true)}
          theme={theme}
          onToggleTheme={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
        />

        <TabNav activeTab={activeTab} onTabChange={setActiveTab} />

        <main className="pt-6">
          {/* Mask Tab */}
          {activeTab === 'redact' && (
            <div id="redact-panel" role="tabpanel">
              <TextareaToolbar>
                <button
                  onClick={clearRedactTab}
                  disabled={!originalText}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 dark:text-neutral-400 dark:hover:text-blue-400 disabled:text-gray-300 dark:disabled:text-neutral-600 disabled:cursor-not-allowed transition"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear
                </button>
                {maskedText && (
                  isEditing ? (
                    <button
                      onClick={() => { setIsEditing(false); document.activeElement?.blur() }}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 font-medium transition"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      Done
                    </button>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 dark:text-neutral-400 dark:hover:text-blue-400 transition"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                      </svg>
                      Edit
                    </button>
                  )
                )}
                <label
                  className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border transition font-medium ${
                    isProcessing
                      ? 'text-gray-400 dark:text-neutral-600 border-gray-200 dark:border-neutral-700 cursor-wait'
                      : 'text-gray-600 dark:text-neutral-300 border-gray-300 dark:border-neutral-600 hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-500 dark:hover:text-blue-400 cursor-pointer'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                  {isProcessing ? 'Processing...' : 'Upload PDF'}
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfUpload}
                    disabled={isProcessing}
                    className="hidden"
                  />
                </label>
                <div className="flex-1" />
                <PdfBadge info={pdfInfo} />
                <button
                  onClick={() => {
                    const textToCopy = instructions.trim()
                      ? `${instructions.trim()}\n\n${maskedText}`
                      : maskedText
                    copyToClipboard(textToCopy, 'masked')
                  }}
                  disabled={!maskedText}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 dark:disabled:bg-neutral-700 disabled:text-gray-400 dark:disabled:text-neutral-500 disabled:cursor-not-allowed rounded text-white font-medium transition"
                >
                  {copiedField === 'masked' ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      Copied
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </TextareaToolbar>

              <ErrorAlert message={error} />

              {/* Instructions textarea */}
              <div className="mb-3">
                <label className="text-xs text-gray-500 dark:text-neutral-400 mb-1 block">
                  Context for AI (copied with your text)
                </label>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Add instructions for the AI..."
                  className="w-full p-3 bg-gray-50 dark:bg-neutral-800/50 border border-gray-200 dark:border-neutral-700 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 dark:text-neutral-300 placeholder-gray-400 dark:placeholder-neutral-500"
                  rows={2}
                  aria-label="Instructions"
                />
              </div>

              {maskedText && !isEditing ? (
                /* Span view — tap any word to redact or restore */
                <WordSpanRenderer
                  text={maskedText}
                  onWordClick={toggleMaskWord}
                  className="w-full p-4 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg font-mono text-sm whitespace-pre-wrap break-words select-none text-gray-800 dark:text-neutral-200 cursor-default"
                  style={{ minHeight: '24rem' }}
                />
              ) : (
                /* Input / edit mode — textarea */
                <div className="relative">
                  {!maskedText && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10 pointer-events-none rounded-lg">
                      <div className="text-center">
                        <p className="text-sm text-gray-400 dark:text-neutral-500">Paste or type your text to get started</p>
                        <p className="mt-1 text-xs text-gray-300 dark:text-neutral-600">Tap or click any word to redact it</p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-300 dark:text-neutral-600">
                        <span className="w-8 h-px bg-gray-300 dark:bg-neutral-600" />
                        or
                        <span className="w-8 h-px bg-gray-300 dark:bg-neutral-600" />
                      </div>
                      <label className="pointer-events-auto cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-600 dark:text-blue-400 font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 transition">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                        Upload PDF
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={handlePdfUpload}
                          disabled={isProcessing}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-gray-300 dark:text-neutral-600">Up to 50 MB. Text is extracted automatically.</p>
                    </div>
                  )}
                  <HighlightedTextarea
                    value={maskedText}
                    onChange={handleTextChange}
                    onDoubleClick={handleDoubleClick}
                    onContextMenu={handleContextMenu}
                    onBlur={() => setTimeout(() => setIsEditing(false), 200)}
                    placeholder=""
                    className="w-full p-4 bg-transparent border border-gray-200 dark:border-neutral-700 rounded-lg font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 dark:text-neutral-200 placeholder-gray-400 dark:placeholder-neutral-500 relative"
                    style={{ minHeight: '24rem' }}
                    aria-label="Text input"
                  />
                </div>
              )}
            </div>
          )}

          {/* Unmask Tab */}
          {activeTab === 'unmask' && (
            <div id="unmask-panel" role="tabpanel">
              <TextareaToolbar>
                <button
                  onClick={clearUnmaskTab}
                  disabled={!textToUnmask && !unmaskedText}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 dark:text-neutral-400 dark:hover:text-blue-400 disabled:text-gray-300 dark:disabled:text-neutral-600 disabled:cursor-not-allowed transition"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear
                </button>
                <button
                  onClick={() => copyToClipboard(unmaskedText || textToUnmask, 'unmasked')}
                  disabled={!textToUnmask && !unmaskedText}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 dark:text-neutral-400 dark:hover:text-blue-400 disabled:text-gray-300 dark:disabled:text-neutral-600 disabled:cursor-not-allowed transition"
                >
                  {copiedField === 'unmasked' ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      Copied
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
                <div className="flex-1" />
                <button
                  onClick={handleUnmask}
                  disabled={!textToUnmask || mapEntries.length === 0}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 dark:disabled:bg-neutral-700 disabled:text-gray-400 dark:disabled:text-neutral-500 disabled:cursor-not-allowed rounded text-white font-medium transition"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                  </svg>
                  Restore
                </button>
              </TextareaToolbar>

              <textarea
                value={unmaskedText || textToUnmask}
                onChange={(e) => {
                  setTextToUnmask(e.target.value)
                  setUnmaskedText('')
                }}
                placeholder="Paste redacted text here, then click Restore..."
                className="w-full p-4 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 dark:text-neutral-200 placeholder-gray-400 dark:placeholder-neutral-500"
                style={{ minHeight: '24rem' }}
                aria-label="Text input"
              />
            </div>
          )}
        </main>

        <footer className="mt-12 pt-8 border-t border-gray-200 dark:border-neutral-700">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            <div className="text-center">
              <svg className="w-6 h-6 mx-auto text-gray-400 dark:text-neutral-500 mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <h4 className="text-sm font-medium text-gray-700 dark:text-neutral-300">Client-Side Processing</h4>
              <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400 leading-relaxed">All redaction happens in your browser. No servers are involved at any point.</p>
            </div>
            <div className="text-center">
              <svg className="w-6 h-6 mx-auto text-gray-400 dark:text-neutral-500 mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
              <h4 className="text-sm font-medium text-gray-700 dark:text-neutral-300">Zero Data Collection</h4>
              <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400 leading-relaxed">Your text is never seen, stored, or transmitted. Verify it yourself in DevTools.</p>
            </div>
            <div className="text-center">
              <svg className="w-6 h-6 mx-auto text-gray-400 dark:text-neutral-500 mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
              <h4 className="text-sm font-medium text-gray-700 dark:text-neutral-300">Verifiably Private</h4>
              <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400 leading-relaxed">Open your browser's Network tab while using arisk.ai. Zero requests with your data.</p>
            </div>
          </div>
          <div className="pt-4 border-t border-gray-100 dark:border-neutral-800 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-400 dark:text-neutral-500">
            <Link
              to="/privacy"
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              Privacy Policy
            </Link>
            <span aria-hidden="true">&middot;</span>
            <Link
              to="/terms"
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              Terms of Service
            </Link>
            <span aria-hidden="true">&middot;</span>
            <Link
              to="/blog"
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              Blog
            </Link>
            <span aria-hidden="true">&middot;</span>
            <Link
              to="/faq"
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              FAQ
            </Link>
            <span aria-hidden="true">&middot;</span>
            <span>&copy; {new Date().getFullYear()} arisk.ai</span>
          </div>
        </footer>

        <FeedbackModal
          isOpen={showFeedback}
          onClose={() => setShowFeedback(false)}
        />

      </div>
    </div>
  )
}
